import { useFieldArray, useFormContext, useWatch } from 'react-hook-form'

import { Button } from '@/components/ui/button'
import { ErrorMessage } from '@/components/ui/error-message'
import { Label } from '@/components/ui/label'

import { DayHoursRow } from './day-hours-row'
import {
  SCHEDULE_END_MINUTES,
  SCHEDULE_START_MINUTES,
  SCHEDULE_STEP_MINUTES,
} from './week-schedule.constants'
import * as S from './week-schedule.styled'
import type { WeekScheduleDay, WeekScheduleProps } from './week-schedule.types'

// Note: day chips (Su-Sa) + per-day "from / to" hour selectors, shared by Packages and
// Maintenance settings. Bound to a `WeekScheduleDay[]` field array of the surrounding form.
export const WeekSchedule = ({
  name,
  labels,
  daysError,
  variant = 'packages',
  minDurationMinutes = SCHEDULE_STEP_MINUTES,
}: WeekScheduleProps) => {
  const { setValue, getValues } = useFormContext()
  const days = useWatch({ name }) as WeekScheduleDay[] | undefined
  const { fields } = useFieldArray({ name })

  const handleSelectDay = (index: number) => {
    const day = getValues(`${name}.${index}`) as WeekScheduleDay
    setValue(
      `${name}.${index}`,
      {
        ...day,
        enabled: !day.enabled,
        endTime: day.endTime ?? SCHEDULE_END_MINUTES,
        startTime: day.startTime ?? SCHEDULE_START_MINUTES,
      },
      { shouldDirty: true, shouldValidate: !!daysError },
    )
  }

  return (
    <>
      <S.Section $variant={variant}>
        <Label>{labels.days}</Label>
        <S.DayButtons>
          {fields.map((field, index) => (
            <Button
              key={field.id}
              onlyCircle
              onlyBox
              use={days?.[index]?.enabled ? 'primary' : 'secondary'}
              size="medium"
              onClick={() => handleSelectDay(index)}
            >
              {days?.[index]?.name}
            </Button>
          ))}
        </S.DayButtons>
        <ErrorMessage>{daysError}</ErrorMessage>
      </S.Section>

      <S.Section $variant={variant}>
        <Label>{labels.hours}</Label>
        <S.WeekHours>
          {fields.map((field, index) => (
            <DayHoursRow
              key={field.id}
              name={name}
              index={index}
              labels={labels}
              variant={variant}
              minDurationMinutes={minDurationMinutes}
            />
          ))}
        </S.WeekHours>
      </S.Section>
    </>
  )
}
